import type { ProviderOption } from '../../form/schema';
import type { JudgeTemplate, JudgeTemplateListItem } from '../../types';

import { providerToDefaultModel } from './constants';

export type BuildJudgeTemplateOptions = Omit<JudgeTemplate, 'provider' | 'model'>;

export type BuildJudgeTemplateOverrides = Partial<
  Record<
    ProviderOption,
    Partial<{
      messages: JudgeTemplate['messages'];
      tools: JudgeTemplate['tools'];
      model: JudgeTemplate['model'];
    }>
  >
>;

export type BuildJudgeTemplateResult = JudgeTemplateListItem & {
  getTemplateByProvider: (provider: ProviderOption) => JudgeTemplate;
};

/**
 * Builds a judge template configuration for all model providers.
 *
 * Generates provider-specific templates with default models. Supports provider-specific overrides for `model`, `messages`, and `tools`.
 *
 * @param options - Base template options used for all providers.
 * @param overrides - Optional provider-specific overrides. Each provider override can partially specify
 *   `model`, `messages`, and/or `tools`. Properties not specified will fall back to the base options.
 *
 * @returns A BuildJudgeTemplateResult object containing:
 *   - `key`: The template key from options
 *   - `name`: The template name from options
 *   - `description`: The template description from options
 *   - `evaluationMetricKey`: The evaluation metric key from options
 *   - `getTemplateByProvider`: A function that computes and returns a complete JudgeTemplate
 *     for the given provider. Uses provider-specific model defaults or overrides.
 *     All templates support all providers in providerOptions.
 *
 * @example
 * ```ts
 * const template = buildJudgeTemplate(
 *   {
 *     key: 'accuracy',
 *     name: 'Accuracy Judge',
 *     description: 'Evaluates response accuracy',
 *     evaluationMetricKey: '$ld:ai:judge:accuracy',
 *     isInverted: false,
 *     messages: [{ role: 'system', content: 'You are an accuracy judge.' }],
 *   },
 *   {
 *     OpenAI: {
 *       messages: [{ role: 'system', content: 'OpenAI-specific accuracy judge prompt.' }],
 *     },
 *     Anthropic: {
 *       messages: [{ role: 'system', content: 'Anthropic-specific accuracy judge prompt.' }],
 *       model: {
 *         modelName: 'Anthropic.claude-3-opus',
 *         parameters: {},
 *         custom: {},
 *       },
 *       tools: [
 *         {
 *           key: 'score-accuracy',
 *           version: 1,
 *           type: 'object',
 *           description: 'Scores the accuracy of a response on a 0.0-1.0 scale',
 *           schema: {
 *             type: 'object',
 *             properties: {
 *               score: {
 *                 type: 'number',
 *                 description: 'Accuracy score between 0.0 and 1.0',
 *               },
 *               reasoning: {
 *                 type: 'string',
 *                 description: 'Explanation for the accuracy score',
 *               },
 *             },
 *             required: ['score'],
 *             additionalProperties: false,
 *           },
 *         },
 *       ],
 *     },
 *   }
 * );
 * ```
 */
export function buildJudgeTemplate(
  options: BuildJudgeTemplateOptions,
  overrides: BuildJudgeTemplateOverrides = {},
): BuildJudgeTemplateResult {
  return {
    key: options.key,
    name: options.name,
    description: options.description,
    evaluationMetricKey: options.evaluationMetricKey,
    isInverted: options.isInverted,

    getTemplateByProvider: (provider: ProviderOption) => {
      const model = overrides[provider]?.model || providerToDefaultModel[provider];

      return {
        ...options,
        provider,
        model,
        messages: overrides[provider]?.messages || options.messages,
        tools: overrides[provider]?.tools || options.tools,
      };
    },
  };
}
